"use client";

import { FC, useState } from "react";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import { A11y } from "swiper/modules";
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react";
import { Swiper } from "swiper";
import "swiper/css";

import { IconType, ModelTypes } from "@types";
import { CarouselSectionRecord } from "@graphql/types/graphql";

import { ArticleTeaser } from "components/molecules/Teaser/ArticleTeaser";
import { CBTeaser } from "components/molecules/Teaser/CBTeaser";
import { PageTeaser } from "components/molecules/Teaser/PageTeaser";
import { OfficeTeaser } from "components/molecules/Teaser/OfficeTeaser";
import { Icon } from "components/atoms/Icon/Icon";
import { Pretitle } from "components/atoms/Pretitle/Pretitle";
import { Container } from "components/organisms/Container/Container";

import { CBButton } from "components/InlineBlocks/CBButton";
import { StructuredText } from "components/sections/RichtextSection/StructuredText";

import s from "./CarouselSection.module.scss";

interface Props {
  data: CarouselSectionRecord;
}

export const CarouselSection: FC<Props> = ({
  data: { pretitle, title, description, teasers, linkedTeasers, button },
}) => {
  const t = useTranslations("common");

  const [swiper, setSwiper] = useState<Swiper | null>(null);
  const [begin, setBegin] = useState(true);
  const [end, setEnd] = useState(false);

  return (
    <Container>
      <div className={s.Carousel}>
        <div className={s.Inner}>
          <Pretitle pretitle={pretitle ?? undefined} title={title ?? undefined} />
          {description && (
            <div className="prose">
              <StructuredText structuredText={description as any} />
            </div>
          )}
        </div>
        <div className={s.Swiper}>
          <SwiperComponent
            spaceBetween={16}
            slidesPerView="auto"
            breakpoints={{
              768: {
                width: 768,
                spaceBetween: 24,
              },
            }}
            modules={[A11y]}
            onSwiper={(swiperItem) => {
              setSwiper(swiperItem);
            }}
            onSlideChange={(swiperItem) => {
              setBegin(swiperItem.isBeginning);
              setEnd(swiperItem.isEnd);
            }}
          >
            {linkedTeasers.map((teaser) => {
              switch (teaser.__typename) {
                case ModelTypes.ServiceRecord:
                  return (
                    <SwiperSlide className={s.Slide} key={teaser.id}>
                      <PageTeaser page={teaser} />
                    </SwiperSlide>
                  );
                case ModelTypes.ArticleRecord:
                  return (
                    <SwiperSlide className={s.Slide} key={teaser.id}>
                      <ArticleTeaser article={teaser} isHighlighted />
                    </SwiperSlide>
                  );
                case ModelTypes.OfficeRecord:
                  return (
                    <SwiperSlide className={s.Slide} key={teaser.id}>
                      <OfficeTeaser office={teaser} />
                    </SwiperSlide>
                  );
                case ModelTypes.ExpertiseRecord:
                  return (
                    <SwiperSlide className={s.Slide} key={teaser.id}>
                      <PageTeaser pretitle={t("expertise")} page={teaser} />
                    </SwiperSlide>
                  );
                case ModelTypes.TargetGroupRecord:
                  return (
                    <SwiperSlide className={s.Slide} key={teaser.id}>
                      <PageTeaser
                        pretitle={
                          teaser.preTitle && teaser.preTitle !== ""
                            ? teaser.preTitle
                            : t("target-group")
                        }
                        page={teaser}
                      />
                    </SwiperSlide>
                  );
                default:
                  return null;
              }
            })}
            {teasers.map((teaser) => (
              <SwiperSlide className={s.Slide} key={teaser.id}>
                <CBTeaser {...teaser} hasBorder pretitle={pretitle} />
              </SwiperSlide>
            ))}
          </SwiperComponent>
          {linkedTeasers.length + teasers.length > 2 && (
            <div className={s.Actions}>
              <button
                type="button"
                className={classNames(s.CarouselButtonPrevious, {
                  [s.IsDisabled]: begin,
                })}
                onClick={() => swiper?.slidePrev()}
                aria-label={t("previous")}
              >
                <Icon name={IconType.CaretFilled} size="m" />
              </button>
              <button
                type="button"
                className={classNames(s.CarouselButtonNext, {
                  [s.IsDisabled]: end,
                })}
                onClick={() => swiper?.slideNext()}
                aria-label={t("next")}
              >
                <Icon name={IconType.CaretFilled} size="m" />
              </button>
            </div>
          )}
        </div>

        {button?.length > 0 && (
          <div className={s.Buttons}>
            {button?.map((item) => {
              return <CBButton key={item.id} {...item} />;
            })}
          </div>
        )}
      </div>
    </Container>
  );
};
